/*
  Product gallery lightbox (PhotoSwipe v5).

  Replaces the legacy single-image #zoom viewer with a proper gallery: swipe,
  pinch/wheel/double-tap zoom, arrows, counter, thumbnail strip and captions.

  Performance: this module only attaches a delegated click listener on load.
  PhotoSwipe core + CSS are fetched lazily on the FIRST time a customer opens
  the gallery, so a product page that is never zoomed pays no extra weight.

  Configuration is provided by snippets/product-lightbox.liquid via
  window.SAV_PHOTOSWIPE = { core, lightbox, css, thumbnails, captions, syncSlider }.

  Per-gallery behaviour is read from the gallery wrapper:
  - data-zoom-trigger="image"  -> clicking anywhere on the image opens the gallery
  - data-zoom-trigger="button" -> only the magnifying icon opens the gallery
*/

(() => {
  const config = window.SAV_PHOTOSWIPE;
  if (!config || !config.core || !config.lightbox) return;
  // This file may be included by more than one product section on a page;
  // classic scripts aren't de-duplicated like modules, so guard against
  // running the setup (and binding document listeners) twice.
  if (window.__savGalleryLightboxInit) return;
  window.__savGalleryLightboxInit = true;

  // Inject the PhotoSwipe stylesheet once, returning a promise that resolves
  // when it has loaded so the first open is never unstyled.
  let cssPromise = null;
  const loadCSS = () => {
    if (cssPromise) return cssPromise;
    cssPromise = new Promise((resolve) => {
      if (!config.css || document.querySelector('link[data-pswp-css]')) {
        resolve();
        return;
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = config.css;
      link.setAttribute('data-pswp-css', '');
      link.onload = () => resolve();
      link.onerror = () => resolve();
      document.head.appendChild(link);
    });
    return cssPromise;
  };

  // Small set of theme-aware overrides + thumbnail/caption styling, injected once.
  const injectStyles = () => {
    if (document.getElementById('pswp-custom-styles')) return;
    const style = document.createElement('style');
    style.id = 'pswp-custom-styles';
    style.textContent = `
      .pswp {
        /* Match the product-card background so transparent product images sit on
           a light surface instead of PhotoSwipe's default black / #222 placeholder.
           !important so the override wins regardless of stylesheet load order. */
        --pswp-bg: var(--color-background-cards, var(--color-background-main, #ffffff)) !important;
        --pswp-placeholder-bg: var(--color-background-cards, var(--color-background-main, #ffffff)) !important;
        --pswp-icon-color: var(--color-text-main, #1a1a1a);
        --pswp-icon-color-secondary: var(--color-background-cards, #ffffff);
      }
      .pswp__counter { color: var(--color-text-main, #1a1a1a); opacity: .75; text-shadow: none; }
      .pswp__caption-custom { position: absolute; left: 0; right: 0; bottom: 84px; z-index: 10; text-align: center; padding: 6px 16px; font-size: 13px; line-height: 1.4; color: var(--color-text-main, #1a1a1a); opacity: .8; pointer-events: none; }
      .pswp__thumbnails { position: absolute; left: 0; right: 0; bottom: 0; z-index: 10; display: flex; gap: 8px; justify-content: center; align-items: center; padding: 12px; overflow-x: auto; scrollbar-width: none; }
      .pswp__thumbnails::-webkit-scrollbar { display: none; }
      .pswp__thumb { flex: 0 0 auto; width: 54px; height: 54px; padding: 0; border: 2px solid transparent; border-radius: 6px; overflow: hidden; background: none; cursor: pointer; opacity: .55; transition: opacity .15s ease, border-color .15s ease; }
      .pswp__thumb:hover { opacity: .85; }
      .pswp__thumb.is-active { opacity: 1; border-color: var(--color-text-main, #1a1a1a); }
      .pswp__thumb img { display: block; width: 100%; height: 100%; object-fit: cover; }
      @media (max-width: 749px) { .pswp__thumb { width: 44px; height: 44px; } .pswp__caption-custom { bottom: 72px; } }
    `;
    document.head.appendChild(style);
  };

  class ProductGalleryLightbox {
    constructor(gallery) {
      this.gallery = gallery;
      this.trigger = gallery.dataset.zoomTrigger === 'button' ? 'button' : 'image';
      this.lightbox = null;
      this.slides = [];
      this.lastIndex = 0;
      this.gallery.addEventListener('click', this.onClick.bind(this));
      // Warm the (tiny) stylesheet while the browser is idle so the first
      // open is instant, without blocking initial page render.
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
      idle(() => loadCSS());
    }

    onClick(e) {
      let item;
      if (this.trigger === 'button') {
        const btn = e.target.closest('.product-gallery-item__zoom');
        item = btn ? btn.closest('.product-gallery-item[data-pswp-src]') : null;
      } else {
        item = e.target.closest('.product-gallery-item[data-pswp-src]');
      }
      if (!item) return;
      e.preventDefault();
      this.open(item);
    }

    buildSlides() {
      const all = Array.from(this.gallery.querySelectorAll('.product-gallery-item'));
      this.slides = all
        .filter((el) => el.dataset.pswpSrc)
        .map((el) => ({
          src: el.dataset.pswpSrc,
          thumb: el.dataset.pswpThumb || el.dataset.pswpSrc,
          width: parseInt(el.dataset.pswpWidth, 10) || 0,
          height: parseInt(el.dataset.pswpHeight, 10) || 0,
          alt: el.dataset.pswpAlt || '',
          // Index of this image among ALL gallery items (= the slider slide index),
          // so we can sync the page slider on close even when videos are present.
          slideIndex: all.indexOf(el)
        }));
    }

    async open(item) {
      this.buildSlides();
      const all = Array.from(this.gallery.querySelectorAll('.product-gallery-item'));
      const slideIndex = all.indexOf(item);
      const index = this.slides.findIndex((s) => s.slideIndex === slideIndex);
      if (index < 0 || !this.slides.length) return;

      await this.ensureLightbox();
      this.lightbox.loadAndOpen(index, this.slides);
    }

    async ensureLightbox() {
      if (this.lightbox) return;

      // Append the PhotoSwipe stylesheet first, THEN our overrides, so our
      // <style> always comes after photoswipe.css in source order.
      const cssReady = loadCSS();
      injectStyles();
      const [{ default: PhotoSwipeLightbox }] = await Promise.all([
        import(config.lightbox),
        cssReady
      ]);

      const padding = config.thumbnails
        ? { top: 20, bottom: 96, left: 16, right: 16 }
        : { top: 20, bottom: 20, left: 16, right: 16 };

      this.lightbox = new PhotoSwipeLightbox({
        pswpModule: () => import(config.core),
        showHideAnimationType: 'zoom',
        bgOpacity: 1,
        padding,
        wheelToZoom: true,
        loop: true
      });

      if (config.captions || config.thumbnails) {
        this.lightbox.on('uiRegister', () => this.registerUI());
      }

      this.lightbox.on('change', () => {
        if (this.lightbox.pswp) this.lastIndex = this.lightbox.pswp.currIndex;
      });

      if (config.syncSlider) {
        this.lightbox.on('close', () => this.syncSlider());
      }

      this.lightbox.init();
    }

    registerUI() {
      const pswp = this.lightbox.pswp;

      if (config.captions) {
        pswp.ui.registerElement({
          name: 'caption',
          className: 'pswp__caption-custom',
          appendTo: 'root',
          onInit: (el) => {
            const update = () => {
              const alt = pswp.currSlide && pswp.currSlide.data ? pswp.currSlide.data.alt : '';
              el.textContent = alt || '';
              el.style.display = alt ? '' : 'none';
            };
            pswp.on('change', update);
            update();
          }
        });
      }

      if (config.thumbnails && pswp.options.dataSource.length > 1) {
        pswp.ui.registerElement({
          name: 'thumbnails',
          className: 'pswp__thumbnails',
          appendTo: 'root',
          onInit: (el) => {
            pswp.options.dataSource.forEach((slide, i) => {
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'pswp__thumb';
              const img = document.createElement('img');
              img.src = slide.thumb;
              img.alt = slide.alt || '';
              img.loading = 'lazy';
              btn.appendChild(img);
              btn.addEventListener('click', () => pswp.goTo(i));
              el.appendChild(btn);
            });
            const setActive = () => {
              Array.from(el.children).forEach((child, i) => {
                child.classList.toggle('is-active', i === pswp.currIndex);
              });
              const active = el.children[pswp.currIndex];
              // Center the active thumb by scrolling ONLY the strip. Never use
              // scrollIntoView here: it scrolls ancestor elements too, which
              // shifts the whole viewer sideways on the last thumbnail.
              if (active) {
                el.scrollLeft = active.offsetLeft - (el.clientWidth - active.clientWidth) / 2;
              }
            };
            pswp.on('change', setActive);
            setActive();
          }
        });
      }
    }

    // Move the on-page slider to the image the customer ended on, so the two
    // stay in sync after the lightbox closes.
    syncSlider() {
      const slide = this.slides[this.lastIndex];
      if (!slide) return;
      const slider = this.gallery.querySelector('css-slider');
      if (slider && slider.sliderEnabled && typeof slider.changeSlide === 'function') {
        slider.changeSlide(slide.slideIndex);
      }
    }
  }

  const initGallery = (gallery) => {
    if (!gallery || gallery.dataset.lightboxReady) return;
    if (gallery.dataset.zoomViewer !== 'lightbox') return;
    gallery.dataset.lightboxReady = 'true';
    new ProductGalleryLightbox(gallery);
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-js-product-gallery]').forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Re-initialise galleries added/replaced by the theme editor.
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
